import type { DeepPartial } from './types';

type WithoutUndefinedValues<T extends Record<string, unknown>> = {
  [Key in keyof T as undefined extends T[Key] ? never : Key]: T[Key];
} & {
  [Key in keyof T as undefined extends T[Key] ? Key : never]?: Exclude<
    T[Key],
    undefined
  >;
};

export const asRecord = (value: unknown): Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

/** Converts an explicitly empty nullable string to null while preserving omission. */
export const clearable = (
  value: string | null | undefined
): string | null | undefined => (value === '' ? null : value);

/** Omits properties whose value is undefined while retaining null values. */
export const stripUndefined = <T extends Record<string, unknown>>(
  value: T
): WithoutUndefinedValues<T> =>
  Object.fromEntries(
    Object.entries(value).filter(
      ([, propertyValue]) => propertyValue !== undefined
    )
  ) as WithoutUndefinedValues<T>;

export function deepMerge<T extends Record<string, unknown>>(
  base: T,
  overrides: DeepPartial<T>
): T {
  const result = { ...base };
  for (const key of Object.keys(overrides) as (keyof T)[]) {
    const baseVal = base[key];
    const overrideVal = overrides[key as keyof typeof overrides];
    if (
      overrideVal !== undefined &&
      typeof baseVal === 'object' &&
      baseVal !== null &&
      typeof overrideVal === 'object' &&
      overrideVal !== null &&
      !Array.isArray(baseVal) &&
      !Array.isArray(overrideVal)
    ) {
      result[key] = deepMerge(
        baseVal as Record<string, unknown>,
        overrideVal as DeepPartial<Record<string, unknown>>
      ) as T[keyof T];
    } else if (overrideVal !== undefined) {
      result[key] = overrideVal as T[keyof T];
    }
  }
  return result;
}
